import { css } from 'aphrodite';
import * as React from 'react';
import { Industry } from '../../../../extViewmodels';
import { getSubverticalOptions } from '../../../../models/UiUtils';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownSelectTrigger,
} from '../../../../web/components-v2/Dropdown';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { useGhostCalendar } from './context';
import {
	GhostCalendarAccountAgeOptions,
	GhostCalendarAllIndustriesOption,
	GhostCalendarAllSubverticalsOption,
	GhostCalendarCampaignTypeOptions,
	GhostCalendarIndustryOptions,
} from './models';
import { styleSheet } from './styles';

const getSubverticalOptionsForIndustry = (industry: Industry) => {
	const subverticals = getSubverticalOptions(industry);
	return subverticals.map<ISelectOption<string>>(x => {
		return {
			dataContext: x,
			id: `subvertical-option-${x}`,
			text: x,
			type: 'checkbox',
		};
	});
};

export const GhostCalendarFilters: React.FC = () => {
	const { selectedFilterOptions, setSelectedFilterOptions } = useGhostCalendar();

	const subverticalOptions = React.useMemo<ISelectOption[]>(() => {
		const selectedIndustry = selectedFilterOptions.industries?.[0];
		return selectedIndustry?.dataContext ? getSubverticalOptionsForIndustry(selectedIndustry.dataContext) : [];
	}, [selectedFilterOptions]);

	const isSubverticalSelectionEnabled = selectedFilterOptions?.industries?.length === 1;
	const isAllSubverticalsSelected = selectedFilterOptions?.subverticals?.length === subverticalOptions.length;
	const isAllIndustriesSelected = selectedFilterOptions?.industries?.length === GhostCalendarIndustryOptions.length;

	// #region Industries/Verts.
	const onIndustryOptionClicked = React.useCallback(
		(option: ISelectOption, wasSelected: boolean) => {
			const nextSelectedIndustries = [...(selectedFilterOptions.industries || [])];
			const index = nextSelectedIndustries.findIndex(x => x.dataContext === option.dataContext);
			if (!!wasSelected && index < 0) {
				nextSelectedIndustries.push(option);
			} else if (!wasSelected && index >= 0) {
				nextSelectedIndustries.splice(index, 1);
			}

			setSelectedFilterOptions({
				...selectedFilterOptions,
				industries: nextSelectedIndustries,
				subverticals:
					nextSelectedIndustries.length === 1
						? getSubverticalOptionsForIndustry(nextSelectedIndustries[0].dataContext)
						: [],
			});
		},
		[selectedFilterOptions, setSelectedFilterOptions]
	);

	const onAllIndustriesClicked = () => {
		setSelectedFilterOptions({
			...selectedFilterOptions,
			industries: isAllIndustriesSelected ? [] : GhostCalendarIndustryOptions,
			subverticals: [],
		});
	};

	const onSelectSubvertical = React.useCallback(
		(option: ISelectOption<string>, wasSelected: boolean) => {
			const nextSelectedSubverticals = [...(selectedFilterOptions.subverticals || [])];
			const index = nextSelectedSubverticals.findIndex(x => x.dataContext === option.dataContext);
			if (!!wasSelected && index < 0) {
				nextSelectedSubverticals.push(option);
			} else if (!wasSelected && index >= 0) {
				nextSelectedSubverticals.splice(index, 1);
			}

			setSelectedFilterOptions({
				...selectedFilterOptions,
				subverticals: nextSelectedSubverticals,
			});
		},
		[selectedFilterOptions, setSelectedFilterOptions]
	);

	const onAllSubverticalsClicked = () => {
		setSelectedFilterOptions({
			...selectedFilterOptions,
			subverticals: isAllSubverticalsSelected ? [] : subverticalOptions,
		});
	};

	const industriesFilterTitle = React.useMemo(() => {
		const count = selectedFilterOptions.industries?.length || 0;
		if (count === GhostCalendarIndustryOptions.length) {
			return 'All industries';
		}
		if (count === 1) {
			return selectedFilterOptions.industries[0].text;
		}
		return `${count} industries`;
	}, [selectedFilterOptions.industries]);

	const subverticalFilterTitle = React.useMemo(() => {
		const count = selectedFilterOptions.subverticals?.length || 0;
		if (!isSubverticalSelectionEnabled || count === subverticalOptions.length) {
			return 'All subverticals';
		}
		if (count === 1) {
			return selectedFilterOptions.subverticals[0].text;
		}
		return `${count} subverticals`;
	}, [selectedFilterOptions.subverticals, isSubverticalSelectionEnabled, subverticalOptions]);
	// #endregion

	// #region account age
	const [selectedAccountAgeId, setSelectedAccountAgeId] = React.useState<string>(
		selectedFilterOptions.ages?.length
			? (GhostCalendarAccountAgeOptions.find(x => x.dataContext === selectedFilterOptions.ages[0].dataContext)?.id ??
					GhostCalendarAccountAgeOptions[0].id)
			: GhostCalendarAccountAgeOptions[0].id
	);

	const [selectedCampaignTypeId, setSelectedCampaignTypeId] = React.useState<string>(
		GhostCalendarCampaignTypeOptions[0].id
	);

	const onSelectAccountAge = React.useCallback(
		(value: string) => {
			const option = GhostCalendarAccountAgeOptions.find(o => o.id === value);
			if (option) {
				setSelectedAccountAgeId(option.id);
				setSelectedFilterOptions({
					...selectedFilterOptions,
					ages: !option.dataContext ? [] : [option],
				});
			}
		},
		[selectedFilterOptions, setSelectedFilterOptions]
	);

	const onSelectCampaignType = React.useCallback(
		(value: string) => {
			const option = GhostCalendarCampaignTypeOptions.find(o => o.id === value);
			if (option) {
				setSelectedCampaignTypeId(option.id);
				setSelectedFilterOptions({
					...selectedFilterOptions,
					campaignType: !option.dataContext ? [] : [option],
				});
			}
		},
		[selectedFilterOptions, setSelectedFilterOptions]
	);
	// #endregion

	return (
		<div className={css(styleSheet.filtersContainer)}>
			<div className={css(styleSheet.row)}>
				<Dropdown>
					<DropdownSelectTrigger styles={[styleSheet.industrySelect]}>{industriesFilterTitle}</DropdownSelectTrigger>
					<DropdownContent>
						<DropdownCheckboxItem checked={isAllIndustriesSelected} onCheckedChange={onAllIndustriesClicked}>
							{GhostCalendarAllIndustriesOption.text}
						</DropdownCheckboxItem>
						{GhostCalendarIndustryOptions.map(option => (
							<DropdownCheckboxItem
								key={option.id}
								checked={!!selectedFilterOptions.industries?.find(x => x.id === option.id)}
								onCheckedChange={checked => onIndustryOptionClicked(option, checked)}
							>
								{option.text}
							</DropdownCheckboxItem>
						))}
					</DropdownContent>
				</Dropdown>
				<Dropdown>
					<DropdownSelectTrigger
						disabled={!isSubverticalSelectionEnabled}
						styles={[styleSheet.subverticalSelect, !isSubverticalSelectionEnabled && styleSheet.selectDisabled]}
					>
						{subverticalFilterTitle}
					</DropdownSelectTrigger>
					<DropdownContent>
						<DropdownCheckboxItem checked={isAllSubverticalsSelected} onCheckedChange={onAllSubverticalsClicked}>
							{GhostCalendarAllSubverticalsOption.text}
						</DropdownCheckboxItem>
						{subverticalOptions.map(option => (
							<DropdownCheckboxItem
								key={option.id}
								checked={!!selectedFilterOptions.subverticals?.find(x => x.id === option.id)}
								onCheckedChange={checked => onSelectSubvertical(option, checked)}
							>
								{option.text}
							</DropdownCheckboxItem>
						))}
					</DropdownContent>
				</Dropdown>
			</div>
			<Select value={selectedCampaignTypeId} onValueChange={onSelectCampaignType}>
				<SelectTrigger styles={styleSheet.accountAgeSelect}>
					<span>{GhostCalendarCampaignTypeOptions.find(o => o.id === selectedCampaignTypeId)?.text}</span>
				</SelectTrigger>
				<SelectContent>
					{GhostCalendarCampaignTypeOptions.map(option => (
						<SelectItem key={option.id} value={option.id}>
							{option.text}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<Select value={selectedAccountAgeId} onValueChange={onSelectAccountAge}>
				<SelectTrigger styles={styleSheet.accountAgeSelect}>
					<span>{GhostCalendarAccountAgeOptions.find(o => o.id === selectedAccountAgeId)?.text}</span>
				</SelectTrigger>
				<SelectContent>
					{GhostCalendarAccountAgeOptions.map(option => (
						<SelectItem key={option.id} value={option.id}>
							{option.text}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
};
